import React, { useMemo } from "react";
import { Table } from "../../../../../shared/Table/Table";
import classes from "../../../newBusinessInsightsContent.module.scss";
import { useNetRevenueSummary } from "./hooks/useNetRevenueSummary";
import Header from "../../../components/Header";
import { useHeaderActions } from "../../../hooks/useHeaderActions";
import { Box } from "../../../../../shared/Box/Box";
import { Skeleton } from "../../../../../shared/Skeleton/Skeleton";
import { Empty } from "../../../../../shared/Empty/Empty";
import { HTTP_ENDPOINTS } from "../../../../../consts/api";

const modalInfo = {
  title: "netRevenueSummaryReport.info.title",
  rows: [
    "netRevenueSummaryReport.info.clinicSummary",
    "netRevenueSummaryReport.info.providerSummary",
  ],
};

const NetRevenueSummary = () => {
  const { selectedClinic, period, apiPeriod } = useHeaderActions();
  const requestParams = {
    fromDate: apiPeriod.fromDate,
    toDate: apiPeriod.toDate,
    clinic_id:
      selectedClinic.value.value !== 0 ? [selectedClinic.value.value] : [],
  };
  const { data, isLoading } = useNetRevenueSummary(requestParams);

  const isClinicDataEmpty = !isLoading && !data?.clinicNetRevenue?.length;
  const isProviderDataEmpty = !isLoading && !data?.providerNetRevenue?.length;

  const clinicData = useMemo(
    () =>
      data?.clinicNetRevenue?.map((clinic) => ({
        clinicName: clinic.clinic_name,
        totalGrossAmount: clinic.total_gross_amount,
        totalDiscountAmount: clinic.total_discount_amount,
        totalTaxAmount: clinic.total_tax_amount,
        totalNetRevenue: clinic.total_net_revenue,
        totalCtc: clinic.total_ctc,
        totalEgiftRedemption: clinic.total_egift_redemption,
        totalWalletPaymentShare: clinic.total_wallet_payment_share,
      })) || [],
    [data],
  );

  const providerData = useMemo(
    () =>
      data?.providerNetRevenue?.map((provider) => ({
        providerName: provider.provider_name,
        totalGrossAmount: provider.total_gross_amount,
        totalDiscountAmount: provider.total_discount_amount,
        totalTaxAmount: provider.total_tax_amount,
        totalNetRevenue: provider.total_net_revenue,
        totalCtc: provider.total_ctc,
        totalEgiftRedemption: provider.total_egift_redemption,
        totalWalletPaymentShare: provider.total_wallet_payment_share,
      })) || [],
    [data],
  );

  const tableColumns = [
    { data: "Total Gross Amount", accessor: "totalGrossAmount" },
    { data: "Total Discount Amount", accessor: "totalDiscountAmount" },
    { data: "Total Tax Amount", accessor: "totalTaxAmount" },
    { data: "Total Net Revenue", accessor: "totalNetRevenue" },
    { data: "Total CTC", accessor: "totalCtc" },
    { data: "Total eGift Redemption", accessor: "totalEgiftRedemption" },
    { data: "Total Wallet Payment Share", accessor: "totalWalletPaymentShare" },
  ];

  return (
    <div className="flex-col font-14 gap-16">
      <Header
        title="Net Revenue Summary Report"
        selectedClinic={selectedClinic}
        period={period}
        downloadRoute={HTTP_ENDPOINTS.getNetRevenueSummary()}
        requestParams={requestParams}
        modalInfo={modalInfo}
      />
      <div className="flex-col gap-16">
        <div className={classes.tableContainer}>
          <Table
            bgColor="white"
            cols={[
              { data: "Clinic Name", accessor: "clinicName" },
              ...tableColumns,
            ]}
            data={clinicData}
          />
          {isLoading && (
            <Box>
              <Skeleton count={8} height={40.5} borderRadius="0px" />
            </Box>
          )}
          {isClinicDataEmpty && (
            <Box>
              <Empty position="center" />
            </Box>
          )}
        </div>
        <div className={classes.tableContainer}>
          <Table
            bgColor="white"
            cols={[
              { data: "Provider Name", accessor: "providerName" },
              ...tableColumns,
            ]}
            data={providerData}
          />
          {isLoading && (
            <Box>
              <Skeleton count={8} height={40.5} borderRadius="0px" />
            </Box>
          )}
          {isProviderDataEmpty && (
            <Box>
              <Empty position="center" />
            </Box>
          )}
        </div>
      </div>
    </div>
  );
};

export default NetRevenueSummary;
